import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { UploadCloud, ScanLine, Loader2, X, Check, Plus, FileText, Eye, Trash2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, fileUrl } from "@/lib/api";
import { DOC_TYPES, SUPPORTING_DOC_CATEGORIES } from "@/lib/docTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";

const empty = { client_name: "", document_type: "", deed_number: "", deed_date: "", region: "", notes: "", tags: "" };

export default function Upload() {
  const navigate = useNavigate();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState(empty);
  const [clientNames, setClientNames] = useState([]);
  const [clientFocused, setClientFocused] = useState(false);

  // Supporting docs state
  const [supportingDocs, setSupportingDocs] = useState([]);
  // Preview modal state
  const [previewDoc, setPreviewDoc] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    api.get("/clients/names")
      .then(({ data }) => {
        if (cancelled) return;
        setClientNames(data.names || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const clientSuggestions = useMemo(() => {
    const value = form.client_name.trim().toLowerCase();
    if (value.length < 2) return [];
    return clientNames
      .filter((name) => name.toLowerCase().startsWith(value) && name.toLowerCase() !== value)
      .slice(0, 8);
  }, [clientNames, form.client_name]);

  const handleFiles = async (f) => {
    if (!f) return;
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(data);
      toast.success("Berkas terunggah. Lanjutkan isi detail dokumen.");
    } catch (e) {
      toast.error("Gagal mengunggah berkas.");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const runOcr = async () => {
    if (!uploaded) return;
    setOcrLoading(true);
    try {
      const { data } = await api.post("/ocr", { storage_path: uploaded.storage_path });
      setForm((f) => ({
        ...f,
        client_name: data.client_name || f.client_name,
        document_type: data.document_type || f.document_type,
        deed_number: data.deed_number || f.deed_number,
        deed_date: data.deed_date || f.deed_date,
        region: data.region || f.region,
        notes: data.summary || f.notes,
        _ocr_text: data.full_text || "",
      }));
      toast.success("Data berhasil diekstrak dengan OCR. Mohon periksa kembali.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR gagal. Silakan isi manual.");
    } finally {
      setOcrLoading(false);
    }
  };

  // --- Supporting docs ---
  const addSupportingDoc = () => {
    setSupportingDocs((prev) => [...prev, { id: Date.now(), category: "", file: null, uploaded: null, uploading: false }]);
  };

  const updateSupportingDoc = (id, updates) => {
    setSupportingDocs((prev) => prev.map((d) => (d.id === id ? { ...d, ...updates } : d)));
  };

  const removeSupportingDoc = (id) => {
    setSupportingDocs((prev) => prev.filter((d) => d.id !== id));
  };

  const handleSupportingUpload = async (id, f) => {
    if (!f) return;
    updateSupportingDoc(id, { file: f, uploading: true });
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateSupportingDoc(id, { uploaded: data, uploading: false });
      toast.success(`"${f.name}" berhasil diunggah.`);
    } catch (e) {
      toast.error(`Gagal mengunggah "${f.name}".`);
      updateSupportingDoc(id, { file: null, uploading: false });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.document_type) {
      toast.error("Nama klien dan jenis akta wajib diisi.");
      return;
    }
    // Validate supporting docs have categories
    const incompleteDocs = supportingDocs.filter((d) => d.uploaded && !d.category);
    if (incompleteDocs.length > 0) {
      toast.error("Pilih kategori untuk semua dokumen pendukung.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/documents", {
        client_name: form.client_name,
        document_type: form.document_type,
        deed_number: form.deed_number,
        deed_date: form.deed_date,
        region: form.region,
        notes: form.notes,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        storage_path: uploaded?.storage_path || "",
        original_filename: uploaded?.original_filename || "",
        content_type: uploaded?.content_type || "",
        ocr_text: form._ocr_text || "",
        supporting_docs: supportingDocs
          .filter((d) => d.uploaded)
          .map((d) => ({
            category: d.category,
            storage_path: d.uploaded.storage_path,
            original_filename: d.uploaded.original_filename,
            content_type: d.uploaded.content_type,
          })),
      });
      toast.success("Dokumen tersimpan ke arsip.");
      navigate(`/arsip/${data.slug || data._id}`);
    } catch (e) {
      toast.error("Gagal menyimpan dokumen.");
    } finally {
      setSaving(false);
    }
  };

  const isImage = uploaded?.content_type?.startsWith("image/");

  const renderPreviewContent = (doc) => {
    const ext = (doc.original_filename || doc.storage_path || "").split(".").pop()?.toLowerCase();
    const isImg = doc.content_type?.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff"].includes(ext);
    const isPdf = ext === "pdf" || doc.content_type === "application/pdf";
    if (isImg) return <img src={fileUrl(doc.storage_path)} alt="preview" className="max-h-[72vh] w-full object-contain" />;
    if (isPdf) return <iframe src={fileUrl(doc.storage_path)} title="preview" className="w-full h-[72vh] bg-white rounded-sm" />;
    return (
      <div className="text-white/60 text-center px-6 py-20">
        <FileText className="w-16 h-16 mx-auto mb-4 text-gold/60" strokeWidth={1} />
        <div className="text-lg font-medium text-white/80 mb-1">{doc.original_filename}</div>
        <div className="text-sm text-white/40 uppercase tracking-wider">.{ext} — tidak bisa di-preview</div>
      </div>
    );
  };

  return (
    <div className={`p-4 pt-16 sm:p-8 lg:p-12 max-w-5xl mx-auto animate-fade-up ${uploaded ? "pb-32 sm:pb-36 lg:pb-40" : ""}`}>
      <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">Upload Dokumen</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight c-strong mb-1">Tambah Akta Baru</h1>
      <p className="text-sm c-muted mb-6 sm:mb-10">Unggah hasil pindai dokumen lalu lengkapi metadatanya.</p>

      {/* Step 1: Upload Dokumen Utama */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${uploaded ? "bg-emerald2/20 text-emerald2" : "bg-gold text-white"}`}>
            {uploaded ? <Check className="w-4 h-4" /> : "1"}
          </div>
          <span className="font-display text-lg font-medium c-strong">Dokumen Utama</span>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files[0]); }}
          onClick={() => !uploaded && inputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${dragOver ? "border-gold bg-gold/5" : uploaded ? "border-emerald2/30 bg-emerald2/[0.03]" : "c-card hover:border-gold hover:bg-gold/[0.03]"
            }`}
        >
          <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="file-input" onChange={(e) => handleFiles(e.target.files[0])} />
          {uploading ? (
            <div className="py-10 flex flex-col items-center gap-3 c-muted">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
              <span className="text-sm">Mengunggah…</span>
            </div>
          ) : uploaded ? (
            <div className="py-6 flex flex-col items-center gap-3">
              <div className="w-14 h-14 bg-emerald2/10 text-emerald2 dark:text-[#7FBF9F] flex items-center justify-center rounded-full">
                <Check className="w-7 h-7" strokeWidth={2} />
              </div>
              <div className="text-sm font-medium c-strong">{uploaded.original_filename}</div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={(e) => { e.stopPropagation(); setPreviewDoc(uploaded); }}
                  className="text-xs text-gold flex items-center gap-1 hover:underline" data-testid="preview-main-btn">
                  <Eye className="w-3 h-3" /> Preview
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setUploaded(null); setFile(null); }}
                  className="text-xs text-wine dark:text-[#E08585] flex items-center gap-1 hover:underline" data-testid="remove-file-btn">
                  <X className="w-3 h-3" /> Ganti berkas
                </button>
              </div>
            </div>
          ) : (
            <div className="py-10 flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full c-soft border c-border flex items-center justify-center text-gold mb-1">
                <UploadCloud className="w-7 h-7" strokeWidth={1.4} />
              </div>
              <div className="text-sm font-medium c-strong">Seret berkas ke sini atau klik untuk pilih</div>
              <div className="text-xs c-muted">Hasil pindai dokumen — JPG, PNG, atau PDF</div>
            </div>
          )}
        </div>

        {uploaded && isImage && (
          <div className="mt-5 border c-border rounded-xl overflow-hidden bg-white dark:bg-[#141A20] card-elev">
            <img src={URL.createObjectURL(file)} alt="preview" className="w-full max-h-80 object-contain" />
          </div>
        )}

        {uploaded && (
          <button type="button" onClick={runOcr} disabled={ocrLoading || !isImage} data-testid="ocr-btn"
            className="mt-5 w-full bg-ink dark:bg-[#1E262E] text-white py-3 rounded-lg text-sm font-medium hover:bg-ink-soft dark:hover:bg-[#28323b] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-ink/15">
            {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4 text-gold" />}
            {isImage ? "Scan Otomatis (OCR)" : "OCR hanya untuk gambar"}
          </button>
        )}
      </div>

      {/* Step 2: Detail + Supporting Docs — only after upload */}
      {uploaded && (
        <div className="mt-10 animate-fade-up">
          <div className="border-t c-border mb-8" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Left: Metadata form */}
            <form onSubmit={save} id="doc-form" className="c-card border rounded-xl p-6 card-elev space-y-5" data-testid="metadata-form">
              <div className="flex items-center gap-2 pb-1">
                <div className="w-7 h-7 rounded-full bg-gold text-white flex items-center justify-center text-xs font-bold">2</div>
                <span className="font-display text-xl font-medium c-strong">Detail Dokumen</span>
              </div>

              <Field label="Nama Klien / Pihak *">
                <div className="relative">
                  <input
                    value={form.client_name}
                    onChange={(e) => set("client_name", e.target.value)}
                    onFocus={() => setClientFocused(true)}
                    onBlur={() => window.setTimeout(() => setClientFocused(false), 120)}
                    data-testid="input-client-name"
                    className={inputCls}
                    placeholder="Mis. Budi Santoso"
                    autoComplete="off"
                  />
                  {clientFocused && clientSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-lg border c-border c-card shadow-xl">
                      {clientSuggestions.map((name) => (
                        <button
                          key={name}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            set("client_name", name);
                            setClientFocused(false);
                          }}
                          className="block w-full px-4 py-2.5 text-left text-sm c-text transition hover:bg-gold/10 hover:text-gold"
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Jenis Akta *">
                <Select value={form.document_type} onValueChange={(v) => set("document_type", v)}>
                  <SelectTrigger data-testid="input-doc-type" className={selectTriggerCls}>
                    <SelectValue placeholder="— Pilih jenis —" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg max-h-72">
                    {DOC_TYPES.map((t) => <SelectItem key={t} value={t} className="rounded-md">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Nomor Akta">
                  <input value={form.deed_number} onChange={(e) => set("deed_number", e.target.value)} data-testid="input-deed-number" className={`${inputCls} font-mono`} placeholder="No. 12" />
                </Field>
                <Field label="Tanggal Akta">
                  <DatePicker value={form.deed_date} onChange={(v) => set("deed_date", v)} testid="input-deed-date" />
                </Field>
              </div>

              <Field label="Daerah / Wilayah">
                <input value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="input-region" className={inputCls} placeholder="Mis. Jakarta Selatan" />
              </Field>

              <Field label="Tag (pisahkan dengan koma)">
                <input value={form.tags} onChange={(e) => set("tags", e.target.value)} data-testid="input-tags" className={inputCls} placeholder="tanah, sertifikat, 2024" />
              </Field>

              <Field label="Catatan">
                <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-notes" rows={3} className={inputCls} placeholder="Catatan tambahan…" />
              </Field>

            </form>

            {/* Right: Supporting Docs */}
            <div className="space-y-4">
              <div className="c-card border rounded-xl p-6 card-elev" data-testid="supporting-docs-section">
                <div className="flex items-center gap-2 pb-1 mb-4">
                  <div className="w-7 h-7 rounded-full bg-gold/10 text-gold flex items-center justify-center text-xs font-bold">
                    <Paperclip className="w-4 h-4" />
                  </div>
                  <span className="font-display text-xl font-medium c-strong">Dokumen Pendukung</span>
                  <span className="text-xs c-muted ml-auto">{supportingDocs.filter((d) => d.uploaded).length} file</span>
                </div>

                <div className="space-y-3">
                  {supportingDocs.map((doc) => (
                    <SupportingDocItem
                      key={doc.id}
                      doc={doc}
                      onUpload={(f) => handleSupportingUpload(doc.id, f)}
                      onCategoryChange={(cat) => updateSupportingDoc(doc.id, { category: cat })}
                      onRemove={() => removeSupportingDoc(doc.id)}
                      onPreview={() => doc.uploaded && setPreviewDoc(doc.uploaded)}
                    />
                  ))}
                </div>

                <button type="button" onClick={addSupportingDoc} data-testid="add-supporting-btn"
                  className="mt-4 w-full py-3 border-2 border-dashed c-border rounded-lg text-sm c-muted hover:border-gold/40 hover:text-gold transition-all flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" /> Tambah Dokumen Pendukung
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky footer save button — portal to body so fixed works */}
      {uploaded && createPortal(
        <div className="fixed bottom-0 left-0 lg:left-64 right-0 z-[9999] border-t c-border bg-white/80 dark:bg-[#141A20]/80 backdrop-blur-xl">
          <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-4">
            <div className="text-xs c-muted hidden sm:block">Pastikan semua data sudah benar sebelum menyimpan.</div>
            <button type="submit" form="doc-form" disabled={saving} data-testid="save-document-btn"
              className="w-full sm:w-auto sm:min-w-[240px] bg-gold text-white py-3.5 px-8 rounded-lg text-sm font-medium hover:bg-gold-dark transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-gold/25">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2} />}
              Simpan ke Arsip
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:w-auto p-0 rounded-xl gap-0 overflow-hidden max-h-[90vh]" data-testid="preview-dialog">
          <DialogTitle className="sr-only">Preview Dokumen</DialogTitle>
          <DialogDescription className="sr-only">Pratinjau berkas yang diunggah</DialogDescription>
          {previewDoc && (
            <div className="bg-ink min-h-[200px] sm:min-h-[320px] flex items-center justify-center p-4">
              {renderPreviewContent(previewDoc)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Supporting doc row component ---
function SupportingDocItem({ doc, onUpload, onCategoryChange, onRemove, onPreview }) {
  const fileRef = useRef();

  return (
    <div className="border c-border rounded-lg p-3 space-y-2 transition-all" data-testid="supporting-doc-item">
      {/* Category select */}
      <Select value={doc.category} onValueChange={onCategoryChange}>
        <SelectTrigger data-testid="supporting-category" className={`${selectTriggerCls} text-xs`}>
          <SelectValue placeholder="— Pilih kategori —" />
        </SelectTrigger>
        <SelectContent className="rounded-lg max-h-60">
          {SUPPORTING_DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="rounded-md text-xs">{c}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Upload area or uploaded file */}
      {doc.uploading ? (
        <div className="flex items-center gap-2 text-sm c-muted py-2">
          <Loader2 className="w-4 h-4 animate-spin text-gold" /> Mengunggah…
        </div>
      ) : doc.uploaded ? (
        <div className="flex items-center gap-2 py-1">
          <FileText className="w-4 h-4 text-gold shrink-0" strokeWidth={1.6} />
          <span className="text-xs c-strong truncate flex-1">{doc.uploaded.original_filename}</span>
          <button type="button" onClick={onPreview} className="text-xs text-gold hover:underline flex items-center gap-1" data-testid="preview-supporting-btn">
            <Eye className="w-3 h-3" /> Preview
          </button>
          <button type="button" onClick={onRemove} className="text-xs text-wine dark:text-[#E08585] hover:underline flex items-center gap-1" data-testid="remove-supporting-btn">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <>
          <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx" className="hidden" onChange={(e) => onUpload(e.target.files[0])} />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-supporting-btn"
            className="w-full py-2.5 border border-dashed c-border rounded-lg text-xs c-muted hover:border-gold/40 hover:text-gold transition-all flex items-center justify-center gap-1.5">
            <UploadCloud className="w-3.5 h-3.5" /> Pilih berkas
          </button>
        </>
      )}

      {/* Remove entire item (when not uploaded) */}
      {!doc.uploaded && (
        <button type="button" onClick={onRemove} className="text-[10px] text-wine dark:text-[#E08585] hover:underline flex items-center gap-1">
          <X className="w-3 h-3" /> Hapus
        </button>
      )}
    </div>
  );
}

const inputCls = "w-full px-4 py-2.5 border c-input rounded-lg text-sm outline-none focus:border-gold focus:ring-1 focus:ring-gold transition";
const selectTriggerCls = "w-full h-auto px-4 py-2.5 border c-input rounded-lg text-sm focus:border-gold focus:ring-1 focus:ring-gold transition data-[placeholder]:text-[#A0A0A0] shadow-none";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-[0.12em] c-muted mb-1.5">{label}</label>
      {children}
    </div>
  );
}
